import { useState, useCallback, useEffect } from 'react';
import { hiveFundApi } from '../../../services/api';
import api from '../../../services/api';
import type { SubscriptionData } from '../../../services/api';
import { useUser } from '../../../contexts/UserContext';
import type { HiveFundFlowStep, HiveFundInvestResult } from './HiveFundInvestFlow.types';
import { DEFAULT_MIN_INVESTMENT, buildPresetAmounts } from './HiveFundInvestFlow.utils';

const HIVE_FUND_DEAL_ID = (process.env.REACT_APP_HIVE_FUND_DEAL_ID as string) || '';

interface EsignDoc {
  id: string;
  title: string;
  filename: string;
  download_url: string;
  requires_esign: boolean;
}

export interface EligibilityStatus {
  canInvest: boolean;
  reasons: string[];
  nextSteps: string[];
}

export type HiveFundPaymentMethod = 'bank' | 'crypto' | 'wire' | 'investment_account';

/**
 * Third legal acceptance required beyond terms + risk_disclosure, keyed by
 * payment method. Mirrors REQUIRED_DOCUMENTS in backend legalAcceptanceService.
 * 'investment_account' requires no third document.
 */
function thirdAcceptanceFor(method: HiveFundPaymentMethod | null): 'ach_authorization' | 'crypto_irrevocable' | null {
  switch (method) {
    case 'bank':
    case 'wire':
      return 'ach_authorization';
    case 'crypto':
      return 'crypto_irrevocable';
    default:
      return null;
  }
}

export interface UseHiveFundInvestFlowReturn {
  // State
  step: HiveFundFlowStep;
  investmentAmount: number;
  paymentMethod: HiveFundPaymentMethod | null;
  selectedAccountId: string | null;
  agreementAccepted: boolean;
  acceptTerms: boolean;
  acceptRiskDisclosure: boolean;
  acceptAchAuth: boolean;
  acceptCryptoIrrevocable: boolean;
  signatureImage: string | null;
  subscriptionData: SubscriptionData | null;
  isSubmitting: boolean;
  error: string | null;
  investmentResult: HiveFundInvestResult | null;
  eligibility: EligibilityStatus | null;
  // Esign docs
  esignDocs: EsignDoc[];
  signedDocIds: string[];
  requiresEsign: boolean;
  // Dynamic offering data
  minInvestment: number;
  presetAmounts: number[];
  // Setters
  setStep: (step: HiveFundFlowStep) => void;
  setInvestmentAmount: (amount: number) => void;
  setPaymentMethod: (method: HiveFundPaymentMethod) => void;
  setSelectedAccountId: (id: string | null) => void;
  setAgreementAccepted: (accepted: boolean) => void;
  setAcceptTerms: (accepted: boolean) => void;
  setAcceptRiskDisclosure: (accepted: boolean) => void;
  setAcceptAchAuth: (accepted: boolean) => void;
  setAcceptCryptoIrrevocable: (accepted: boolean) => void;
  setSignatureImage: (image: string | null) => void;
  setSubscriptionData: (data: SubscriptionData | null) => void;
  markDocSigned: (docId: string) => void;
  // Actions
  checkEligibility: () => EligibilityStatus;
  canContinueFrom: (step: HiveFundFlowStep) => boolean;
  submitInvestment: () => void;
  retrySubmit: () => void;
  goToPaymentMethod: () => void;
}

export function useHiveFundInvestFlow(): UseHiveFundInvestFlowReturn {
  const { canInvest } = useUser();

  const [step, setStep] = useState<HiveFundFlowStep>('welcome');
  const [investmentAmount, setInvestmentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState<HiveFundPaymentMethod | null>(null);
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);
  const [agreementAccepted, setAgreementAccepted] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptRiskDisclosure, setAcceptRiskDisclosure] = useState(false);
  const [acceptAchAuth, setAcceptAchAuth] = useState(false);
  const [acceptCryptoIrrevocable, setAcceptCryptoIrrevocable] = useState(false);
  const [signatureImage, setSignatureImage] = useState<string | null>(null);
  const [subscriptionData, setSubscriptionData] = useState<SubscriptionData | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [investmentResult, setInvestmentResult] = useState<HiveFundInvestResult | null>(null);
  const [eligibility, setEligibility] = useState<EligibilityStatus | null>(null);
  const [esignDocs, setEsignDocs] = useState<EsignDoc[]>([]);
  const [signedDocIds, setSignedDocIds] = useState<string[]>([]);
  const [minInvestment, setMinInvestment] = useState(DEFAULT_MIN_INVESTMENT);
  const presetAmounts = buildPresetAmounts(minInvestment);

  // Load offering data (minInvest) and esign-required docs on mount
  useEffect(() => {
    // Fetch min investment from the offering
    const loadOffering = async () => {
      try {
        const slug = 'hive-fund';
        const res: any = await api.get(`/offerings/${slug}`);
        const offering = res?.data?.offering || res?.data?.data || res?.data || {};
        if (offering.minInvest && offering.minInvest > 0) {
          setMinInvestment(offering.minInvest);
        }
      } catch {
        // keep default
      }
    };
    void loadOffering();

    if (!HIVE_FUND_DEAL_ID) return;
    const loadDocs = async () => {
      try {
        // Use the invest documents endpoint for proper visibility filtering
        const res: any = await api.get('/deals/hive-fund/invest/documents');
        const data: EsignDoc[] = res?.data?.data || res?.data || [];
        const required = data.filter((d) => d.requires_esign);
        setEsignDocs(required);
      } catch {
        // ignore — flow continues without esign gate if unavailable
      }
    };
    void loadDocs();
  }, []);

  const requiresEsign = esignDocs.length > 0;

  const markDocSigned = useCallback((docId: string) => {
    setSignedDocIds(prev => prev.includes(docId) ? prev : [...prev, docId]);
  }, []);

  const checkEligibility = useCallback((): EligibilityStatus => {
    const result = canInvest();
    setEligibility(result);
    return result;
  }, [canInvest]);

  const canContinueFrom = useCallback(
    (fromStep: HiveFundFlowStep): boolean => {
      switch (fromStep) {
        case 'welcome':
          return true;
        case 'eligibility':
          return eligibility?.canInvest ?? false;
        case 'amount':
          return investmentAmount >= minInvestment;
        case 'paymentMethod':
          return paymentMethod !== null;
        case 'bankSelect':
          return selectedAccountId !== null;
        case 'review': {
          const third = thirdAcceptanceFor(paymentMethod);
          const thirdAccepted =
            third === 'ach_authorization' ? acceptAchAuth
            : third === 'crypto_irrevocable' ? acceptCryptoIrrevocable
            : true;
          return agreementAccepted && acceptTerms && acceptRiskDisclosure && thirdAccepted;
        }
        case 'docEsign':
          return esignDocs.every(d => signedDocIds.includes(d.id));
        case 'signature':
          return signatureImage !== null;
        default:
          return false;
      }
    },
    [eligibility, investmentAmount, minInvestment, paymentMethod, selectedAccountId, agreementAccepted, acceptTerms, acceptRiskDisclosure, acceptAchAuth, acceptCryptoIrrevocable, signatureImage, esignDocs, signedDocIds],
  );

  const doSubmit = useCallback(async () => {
    setIsSubmitting(true);
    setError(null);
    setStep('processing');

    console.log('🚀 doSubmit: selectedAccountId =', selectedAccountId, '| paymentMethod =', paymentMethod);

    try {
      const bankId = paymentMethod === 'bank' ? (selectedAccountId ?? undefined) : undefined;
      const apiMethod = paymentMethod === 'bank' ? 'bank_ach' as const
        : paymentMethod === 'wire' ? 'wire' as const
        : paymentMethod === 'investment_account' ? 'investment_account' as const
        : 'crypto' as const;
      const third = thirdAcceptanceFor(paymentMethod);
      const legalAcceptances: Record<string, boolean> = {
        terms: acceptTerms,
        risk_disclosure: acceptRiskDisclosure,
        ...(third === 'ach_authorization' ? { ach_authorization: acceptAchAuth } : {}),
        ...(third === 'crypto_irrevocable' ? { crypto_irrevocable: acceptCryptoIrrevocable } : {}),
      };
      console.log('🚀 doSubmit payload:', JSON.stringify({ bankAccountId: bankId, paymentMethod: apiMethod, legal_acceptances: legalAcceptances }));
      const response = await hiveFundApi.invest({
        amount: investmentAmount,
        paymentMethod: apiMethod,
        bankAccountId: bankId,
        agreementAccepted: true,
        legal_acceptances: legalAcceptances,
        signatureImage: signatureImage || undefined,
        subscriptionData: subscriptionData || undefined,
      });

      if (!response.success || !response.data) {
        throw new Error(response.message ?? 'Investment failed');
      }

      setInvestmentResult({
        investmentId: response.data.investmentId,
        confirmationCode: response.data.confirmationCode || 'HF-' + Date.now(),
        amount: response.data.amount,
        paymentMethod: response.data.paymentMethod,
        status: response.data.status,
        coinbaseHostedUrl: response.data.coinbase?.hostedUrl,
        wireInstructions: response.data.wireInstructions,
      });
      setStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
      // Stay on 'processing' — ProcessingStep renders error state
    } finally {
      setIsSubmitting(false);
    }
  }, [investmentAmount, paymentMethod, selectedAccountId, signatureImage, subscriptionData, acceptTerms, acceptRiskDisclosure, acceptAchAuth, acceptCryptoIrrevocable]);

  const submitInvestment = useCallback(() => {
    void doSubmit();
  }, [doSubmit]);

  const retrySubmit = useCallback(() => {
    void doSubmit();
  }, [doSubmit]);

  const goToPaymentMethod = useCallback(() => {
    setError(null);
    setStep('paymentMethod');
  }, []);

  return {
    step,
    investmentAmount,
    paymentMethod,
    selectedAccountId,
    agreementAccepted,
    acceptTerms,
    acceptRiskDisclosure,
    acceptAchAuth,
    acceptCryptoIrrevocable,
    signatureImage,
    subscriptionData,
    isSubmitting,
    error,
    investmentResult,
    eligibility,
    esignDocs,
    signedDocIds,
    requiresEsign,
    minInvestment,
    presetAmounts,
    setStep,
    setInvestmentAmount,
    setPaymentMethod,
    setSelectedAccountId,
    setAgreementAccepted,
    setAcceptTerms,
    setAcceptRiskDisclosure,
    setAcceptAchAuth,
    setAcceptCryptoIrrevocable,
    setSignatureImage,
    setSubscriptionData,
    markDocSigned,
    checkEligibility,
    canContinueFrom,
    submitInvestment,
    retrySubmit,
    goToPaymentMethod,
  };
}
